import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";
import { setUserData } from "../../Pages/Auth/LoginSlice";

const initialState = {
  errors: null,
  isLoading: false,
  profileData: null,
  profileDataUpdate: null,
  getprofileWithWork: null,
  profileImage: null,
  activeMode: 'client' // Tracks if user is in 'client' or 'business' mode
};

const ProfileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setGetProfileData: (state, action) => {
      state.profileData = action.payload;
    },
    setProfileWithWork: (state, action) => {
      state.getprofileWithWork = action.payload;
    },
    setProfileImage: (state, action) => {
      state.profileImage = action.payload;
    },
    switchMode: (state, action) => {
      state.activeMode = action.payload;
    }
  },
});

export const getProfileData = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    let URL = "users/profile";
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};

    if (success) {
      dispatch(setGetProfileData(data));
      // ✅ Update profile image from fetched profile data
      if (data?.profilePicture) {
        dispatch(setProfileImage(data.profilePicture));
      }
    } else {
      dispatch(setGetProfileData(null));
    }
  } catch (error) {
    const errorResponse = error?.response;

    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong."
    }));
    dispatch(setGetProfileData(null));
  } finally {
    dispatch(setLoading(false));
  }
}

export const GetProfilewithWorks = (workerId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    let URL = `worker/${workerId}`;
    const response = await axiosInstance.get(URL);
    const { status, data } = response?.data || {};

    if (status === "success") {
      dispatch(setProfileWithWork(data));
    } else {
      dispatch(setProfileWithWork(null));
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong."
    }));
    dispatch(setProfileWithWork(null));
  } finally {
    dispatch(setLoading(false));
  }
}

// Upload profile picture and save to database
export const updateProfilePicture = (imageFile, oldImageKey = null) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (oldImageKey) {
      formData.append('oldImageKey', oldImageKey);
    }

    let URL = "users/profile";
    const response = await axiosInstance.patch(URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const { data, success, message } = response?.data || {};
    if (success) {
      // Backend returns: data.profilePicture (from user object) or data.imageUrl
      const newProfilePictureUrl = data.profilePicture || data.imageUrl;

      // Update profile image in profile slice
      dispatch(setProfileImage(newProfilePictureUrl));

      // ✅ Update login state's userData so Header component reflects the change immediately
      const currentUserData = getState().login?.userData;
      if (currentUserData && newProfilePictureUrl) {
        const updatedUserData = {
          ...currentUserData,
          profilePicture: newProfilePictureUrl
        };
        dispatch(setUserData(updatedUserData));
      }

      // Refresh profile data to get updated profile picture (for profile page)
      dispatch(getProfileData());

      showNotification({
        message: message || "Profile picture updated successfully",
        theme: "light",
        type: "success",
      });
      return Promise.resolve(data);
    } else {
      dispatch(setProfileImage(null));
      showNotification({
        message: message || "Failed to update profile picture",
        theme: "light",
        type: "error",
      });
      return Promise.reject(new Error(message || "Failed to update profile picture"));
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg = errorResponse?.data?.message || error.message || "Something went wrong.";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg
    }));
    dispatch(setProfileImage(null));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return Promise.reject(error);
  } finally {
    dispatch(setLoading(false));
  }
};

// Upload service image (for service providers)
export const uploadServiceImage = (imageFile, serviceId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('serviceId', serviceId);

    let URL = "image/service/upload";
    const response = await axiosInstance.post(URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const { data, status, message } = response?.data || {};
    if (status === "success") {
      showNotification({
        message: message || "Service image uploaded successfully",
        theme: "light",
        type: "success",
      });
      return Promise.resolve(data);
    } else {
      showNotification({
        message: message || "Failed to upload service image",
        theme: "light",
        type: "error",
      });
      return Promise.reject(new Error(message || "Failed to upload service image"));
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg = errorResponse?.data?.message || error.message || "Something went wrong.";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return Promise.reject(error);
  } finally {
    dispatch(setLoading(false));
  }
};

// Legacy function for backward compatibility
export const updateProfileImage = (image) => async (dispatch) => {
  // If image is a File object, use the new function
  if (image instanceof File) {
    return dispatch(updateProfilePicture(image));
  }
  // Otherwise, use the old logic
  dispatch(setLoading(true));
  try {
    let URL = "image/upload";
    const response = await axiosInstance.post(URL, image);
    const { data, status, message } = response?.data || {};
    if (status === "success") {
      dispatch(setProfileImage(data));
      showNotification({
        message: message || "Image uploaded successfully",
        theme: "light",
        type: "success",
      });
    } else {
      dispatch(setProfileImage(null));
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong."
    }));
    dispatch(setProfileImage(null));
  } finally {
    dispatch(setLoading(false));
  }
};



export const updateProfileData = (updatedData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    let URL = "users/profile";
    const response = await axiosInstance.patch(URL, updatedData)
    const { success, message } = response?.data || {};

    if (success) {
      dispatch(setGetProfileData(response?.data?.data));
      showNotification({
        message: message || "Profile updated successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Something went wrong";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setErrors(error?.response));
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Something went wrong.";

    // Check if it's a duplicate email or phone error
    const errorMessage = errorMsg.toLowerCase();
    let fieldError = null;

    if (errorMessage.includes('email') && (errorMessage.includes('already') || errorMessage.includes('registered') || errorMessage.includes('exist'))) {
      fieldError = { field: 'email', message: errorMsg };
    } else if (errorMessage.includes('phone') && (errorMessage.includes('already') || errorMessage.includes('registered') || errorMessage.includes('exist'))) {
      fieldError = { field: 'phone', message: errorMsg };
    }

    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });

    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setLoading(false));
  }
};

// Format phone number with country code 91 (India)
const formatPhoneWithCountryCode = (phone) => {
  if (!phone) return null;

  // Remove any spaces, dashes, or other characters
  let cleaned = phone.replace(/\D/g, '');

  // If already starts with 91, return as is
  if (cleaned.startsWith('91') && cleaned.length >= 12) {
    return cleaned;
  }

  // If starts with 0, remove it
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // Add 91 prefix if not present
  if (!cleaned.startsWith('91')) {
    cleaned = '91' + cleaned;
  }

  return cleaned;
};

// Send OTP to WhatsApp for phone verification using backend API (which uses WabaConnect)
export const sendPhoneVerificationOTP = (phone) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    // Format phone number with country code
    const formattedPhone = formatPhoneWithCountryCode(phone);

    if (!formattedPhone || formattedPhone.length < 12) {
      const errorMsg = "Please enter a valid phone number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Call backend API to send OTP via WabaConnect
    const URL = "users/profile/phone/otp/request";
    const response = await axiosInstance.post(URL, { phone: formattedPhone });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent to your WhatsApp number",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to send OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to send OTP. Please try again.";

    // Check if it's a duplicate phone error
    const errorMessage = errorMsg.toLowerCase();
    let fieldError = null;

    if (errorMessage.includes('phone') && (errorMessage.includes('already') || errorMessage.includes('registered') || errorMessage.includes('exist'))) {
      fieldError = { field: 'phone', message: errorMsg };
    }

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setLoading(false));
  }
};

// Verify phone number with OTP
export const verifyPhoneNumber = (phone, otp) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    // Format phone number with country code
    const formattedPhone = formatPhoneWithCountryCode(phone);

    if (!formattedPhone) {
      const errorMsg = "Invalid phone number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Call backend API to verify OTP
    const URL = "users/profile/phone/otp/verify";
    const response = await axiosInstance.post(URL, {
      phone: formattedPhone,
      otp: otp
    });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      // Refresh profile data to get updated verification status
      dispatch(getProfileData());
      showNotification({
        message: message || "Phone number verified successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      showNotification({
        message: message || "OTP verification failed",
        theme: "light",
        type: "error",
      });
      return { success: false, message };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Invalid OTP. Please try again.";
    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Send OTP to email for email verification
export const sendEmailVerificationOTP = (email) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    if (!email || !email.includes('@')) {
      const errorMsg = "Please enter a valid email address";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Call backend API to send OTP via email
    const URL = "users/profile/email/otp/request";
    const response = await axiosInstance.post(URL, { email });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent to your email address",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to send OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to send OTP. Please try again.";

    // Check if it's a duplicate email error
    const errorMessage = errorMsg.toLowerCase();
    let fieldError = null;

    if (errorMessage.includes('email') && (errorMessage.includes('already') || errorMessage.includes('registered') || errorMessage.includes('exist'))) {
      fieldError = { field: 'email', message: errorMsg };
    }

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setLoading(false));
  }
};

// Verify email address with OTP
export const verifyEmailNumber = (email, otp) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    if (!email || !email.includes('@')) {
      const errorMsg = "Invalid email format.";
      showNotification({ message: errorMsg, type: "error" });
      return { success: false, message: errorMsg };
    }

    // Call backend endpoint to verify OTP
    const URL = "users/profile/email/otp/verify";
    const response = await axiosInstance.post(URL, { email, otp });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      // Refresh profile data to get updated verification status
      dispatch(getProfileData());
      showNotification({
        message: message || "Email verified successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      showNotification({
        message: message || "OTP verification failed",
        theme: "light",
        type: "error",
      });
      return { success: false, message };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Invalid OTP. Please try again.";
    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};



export const {
  setErrors,
  setLoading,
  setGetProfileData,
  setProfileWithWork,
  setProfileImage,
  switchMode
} = ProfileSlice.actions;

// updateProfilePicture and uploadServiceImage are already exported above with export const
// No need to export them again

// Send OTP to WhatsApp for WhatsApp number verification
export const sendWhatsAppVerificationOTP = (whatsappNumber) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    // Format phone number with country code
    const formattedPhone = formatPhoneWithCountryCode(whatsappNumber);

    if (!formattedPhone || formattedPhone.length < 12) {
      const errorMsg = "Please enter a valid WhatsApp number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Call backend API to send OTP via WabaConnect
    const URL = "users/profile/whatsapp/otp/request";
    const response = await axiosInstance.post(URL, { whatsappNumber: formattedPhone });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent to your WhatsApp number",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to send OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to send OTP. Please try again.";

    // Check if it's a duplicate WhatsApp number error
    const errorMessage = errorMsg.toLowerCase();
    let fieldError = null;

    if (errorMessage.includes('whatsapp') && (errorMessage.includes('already') || errorMessage.includes('registered') || errorMessage.includes('exist'))) {
      fieldError = { field: 'whatsappNumber', message: errorMsg };
    }

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setLoading(false));
  }
};

// Verify WhatsApp number with OTP
export const verifyWhatsAppNumber = (whatsappNumber, otp) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    // Format phone number with country code
    const formattedPhone = formatPhoneWithCountryCode(whatsappNumber);

    if (!formattedPhone || formattedPhone.length < 12) {
      const errorMsg = "Please enter a valid WhatsApp number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Call backend API to verify OTP
    const URL = "users/profile/whatsapp/otp/verify";
    const response = await axiosInstance.post(URL, {
      whatsappNumber: formattedPhone,
      otp: otp
    });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "WhatsApp number verified successfully",
        theme: "light",
        type: "success",
      });
      // Update profile data with verified WhatsApp number
      dispatch(setGetProfileData(data));
      return { success: true, message, data };
    } else {
      const errorMsg = message || "Failed to verify OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to verify OTP. Please try again.";

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export default ProfileSlice.reducer;
